import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { Input, HelpTooltip, Button, Modal } from 'tt-ui-lib/core';
import { Box, List, ListItem, ListItemButton, MenuItem, Select, Tab, Tabs } from '@mui/material';
import styles from './CompanySelectionModal.module.scss';
import SearchIcon from '@mui/icons-material/Search';
import CompanyBlock from 'components/CompanyBlock/CompanyBlock';
import { useSelectedContext } from 'context/contextSelector';
import { useLazyQuery } from '@apollo/client';
import { SHOW_ACTIVE_USER_COMPANIES } from 'api';

const companyValidateList = {
  companyName: 'Company name',
  establishedDate: 'Establish date',
  addressLocation: 'Country, Town/City, Location, Zip/Postal code',
  address: 'Town/City, Location',
  countryId: 'Country',
  postcode: 'Zip/Postal code',
  industryGroupId: 'Industry group',
  subIndustryId: 'Prime subindustry',
  legalTypeId: 'Company legal type',
};

const menu = [
  { value: 'my', name: 'My company' },
  { value: 'external', name: 'External company' },
];

const colors = {
  blue: '#143471',
  gray: '#212121',
};

const tabStyles = {
  '& .MuiTabs-indicator': { color: colors.blue, backgroundColor: colors.blue },
  '& .MuiTab-root': {
    color: colors.gray,
    fontWeight: 500,
    textTransform: 'none',
    marginRight: '60px',
    padding: '0',
    fontSize: '16px',
    lineHeight: '32px',
  },
  '& .Mui-selected': { color: colors.blue, fontWeight: 700 },
};

const searchTextStyles = {
  border: '1px solid #EFEFEF',
  borderRadius: '12px',
  padding: '0px',
  marginTop: '16px',
  width: '100%',
  fontFamily: 'Montserrat',
  fontStyle: 'normal',
  fontWeight: '400',
  fontSize: '16px',
  lineHeight: '24px',
  color: '#757575',
};
const endpointMarket = process.env.REACT_APP_MARKET || '';

const CompanySelectionModal = (props) => {
  const redirectMarket = (url) => {
    window.location.href = `${endpointMarket}${url}`;
  };
  const { onFinish, open, onClose } = props;
  const { user } = useSelectedContext((context) => context.user);
  const [getCompany, { data: userCompanies }] = useLazyQuery(SHOW_ACTIVE_USER_COMPANIES, {
    fetchPolicy: 'network-only',
  });
  const [companyId, setCompanyId] = useState(null);
  const [extCompanies, setExtCompanies] = useState([]);
  const [tabValue, setTabValue] = useState('my');
  const [searchValue, setSearchValue] = useState('');
  const [isNeedCompanyModalOpen, setIsNeedCompanyModalOpen] = useState(false);
  const [companies, setCompanies] = useState([]);

  const isFilterEnabled =
    (tabValue === 'my' && companies.length > 4) ||
    (tabValue === 'external' && extCompanies.length > 4);

  const onChoiseCompany = () => {
    if (!companyId) return;

    onFinish(companyId);
    setCompanyId(null);
    onClose(false);
  };

  const onTabChange = (event, newValue) => {
    setTabValue(newValue);
    setCompanyId(null);
  };

  const onSelectChange = (event) => {
    setTabValue(event.target.value);
    setCompanyId(null);
  };

  const onSearchChange = (e) => {
    setSearchValue(e.target.value.toLowerCase());
    setCompanyId(null);
  };

  const getCompanyWarning = (c) => {
    const warn = Object.keys(companyValidateList).reduce((res, key) => {
      if (!c[key] && !c.addressLocation[key]) res.push(companyValidateList[key]);
      return res;
    }, []);
    return warn;
  };

  const onCancel = () => {
    setIsNeedCompanyModalOpen(false);
    onClose(true);
  };

  const getCompanyWarningText = (c) =>
    `The company does not have information about such parameters: ${getCompanyWarning(c).join(
      ','
    )}`;

  useEffect(() => {
    if (!user.id) return;
    getCompany();
  }, [user]);

  useEffect(() => {
    if (!userCompanies || !userCompanies.showActiveUserCompanies) return;
    if (userCompanies.showActiveUserCompanies) {
      const companyList = userCompanies.showActiveUserCompanies;
      if (!companyList.length) setIsNeedCompanyModalOpen(true);
      else setCompanies([...companyList]);
    }
  }, [userCompanies]);

  useEffect(() => console.log('companyId', companyId), [companyId]);

  return (
    <>
      <Modal
        open={isNeedCompanyModalOpen}
        onClose={onCancel}
        onClick={(e) => e.stopPropagation()}
        title="Create a&nbsp;company profile"
        className={styles.modal}
      >
        <div className={styles.text}>
          In order to use this functionality, please create a profile of your company.
        </div>
        <div className={clsx(styles.buttonBlock, styles.companyBB)}>
          <Button type="default" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="primary" onClick={() => redirectMarket('/dashboard/companiesList')}>
            Create a company profile
          </Button>
        </div>
      </Modal>
      <Modal
        open={open}
        onClose={() => onClose(true)}
        onClick={(e) => e.stopPropagation()}
        title="Select company"
        className={styles.modal}
      >
        <Box sx={{ width: '100%', typography: 'body1', marginBottom: '12px' }}>
          <div className={styles.tabWrapper}>
            <Tabs
              value={tabValue}
              onChange={onTabChange}
              aria-label="companies tabs"
              sx={{ ...tabStyles }}
            >
              {menu.map((item) => (
                <Tab key={item.value} label={item.name} value={item.value} />
              ))}
            </Tabs>
          </div>
          <div className={styles.selectWrapper}>
            <Select
              value={tabValue}
              className={styles.selectType}
              onChange={onSelectChange}
              size="medium"
              sx={{
                '.MuiOutlinedInput-notchedOutline': { border: '0 !important' },
                '.MuiSelect-select': { padding: '12px 0 12px 18px !important' },
              }}
            >
              {menu.map((item) => (
                <MenuItem
                  className={clsx(tabValue === item.value ? styles.active : '')}
                  key={item.value}
                  value={item.value}
                >
                  {item.name}
                </MenuItem>
              ))}
            </Select>
          </div>
          {isFilterEnabled && (
            <Input
              className={clsx(styles.filter, styles.searchFilter)}
              suffix={<SearchIcon />}
              placeholder="Search"
              value={searchValue}
              onChange={onSearchChange}
            />
          )}
        </Box>
        <div className={clsx(styles.listWrapper, isFilterEnabled && styles.filter)}>
          {(companies.length > 0 || extCompanies.length > 0) && (
            <List sx={{ width: '100%', paddingRight: '10px' }}>
              {(tabValue === 'my' ? companies : extCompanies)
                .filter((item) => item.companyName.toLowerCase().includes(searchValue))
                .map((com, idx) => (
                  <ListItem key={`${idx.toString()}${com.companyName}`} alignItems="flex-start">
                    <ListItemButton
                      className={clsx(styles.listItem, com.id === companyId && styles.selected)}
                      disabled={getCompanyWarning(com).length > 0}
                      onClick={() => setCompanyId((value) => (value === com.id ? null : com.id))}
                    >
                      <CompanyBlock company={com} />
                    </ListItemButton>
                    {!!getCompanyWarning(com).length && (
                      <HelpTooltip tooltipText={getCompanyWarningText(com)} />
                    )}
                  </ListItem>
                ))}
            </List>
          )}
          {tabValue === 'my' && companies.length === 0 && <div>There are no companies yet</div>}
          {tabValue === 'external' && extCompanies.length === 0 && (
            <div>There are no external companies yet</div>
          )}
        </div>
        <div className={styles.buttonBlock}>
          <Button type="default" onClick={() => onClose(true)}>
            Cancel
          </Button>
          <Button type="primary" onClick={onChoiseCompany} disabled={!companyId}>
            Select
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default CompanySelectionModal;
